/* Advanced Parallax Engine for FiGGYZ Showcase */
/* Apple-inspired smooth scrolling effects */

class ParallaxEngine {
  constructor() {
    this.elements = [];
    this.rafId = null;
    this.lastScrollY = 0;
    this.currentScrollY = 0;
    this.smoothScrollY = 0;
    this.scrollSpeed = 0.1;
    this.isScrolling = false;
    this.scrollTimeout = null;
    
    this.init();
  }

  init() {
    this.setupElements();
    this.bindEvents();
    this.startAnimation();
  }

  setupElements() {
    // Hero parallax layers
    this.addElement('.hero-content', {
      speed: 0.5,
      offset: 0,
      fadeOut: true,
      scale: true
    });

    this.addElement('.hero-product', {
      speed: 0.3,
      offset: 0,
      rotate: true,
      perspective: 1000
    });

    // Section transitions
    this.addElement('.section-title', {
      speed: 0.8,
      offset: 100,
      fadeIn: true,
      splitText: false  // Disable text splitting for now
    });

    this.addElement('.problem-grid', {
      speed: 0.7,
      offset: 50,
      stagger: true,
      children: '.problem-item'
    });

    this.addElement('.feature-card', {
      speed: 0.9,
      offset: 30,
      stagger: true,
      scale: true,
      delay: 100
    });

    this.addElement('.magmount-demo', {
      speed: 0.6,
      offset: 0,
      rotate3d: true
    });

    // Removed .mount-item parallax - no longer needed

    this.addElement('.chase-tier', {
      speed: 0.85,
      offset: 40,
      stagger: true,
      tilt: true
    });

    // Background elements
    this.addElement('.particle', {
      speed: 0.2,
      offset: 0,
      loop: true
    });
  }

  addElement(selector, options = {}) {
    const elements = document.querySelectorAll(selector);
    elements.forEach((el, index) => {
      const rect = el.getBoundingClientRect();
      const elementData = {
        element: el,
        selector: selector,
        options: options,
        originalY: rect.top + window.pageYOffset,
        originalX: rect.left + window.pageXOffset,
        height: rect.height,
        width: rect.width,
        index: index
      };

      // Apply initial states
      if (options.fadeIn || options.fadeOut) {
        el.style.opacity = options.fadeIn ? '0' : '1';
      }

      if (options.scale) {
        el.style.transform = 'scale(0.9)';
      }

      if (options.splitText && el.textContent) {
        this.splitTextAnimation(el);
      }

      this.elements.push(elementData);
    });
  }

  splitTextAnimation(element) {
    const text = element.textContent.trim();
    // Only split if there's actual text content
    if (!text) return;
    
    const words = text.split(/\s+/).filter(word => word.length > 0);
    element.innerHTML = words.map((word, i) => 
      `<span class="parallax-word" style="display: inline-block; transition-delay: ${i * 50}ms">${word}</span>`
    ).join(' ');
  }

  bindEvents() {
    // PERFORMANCE: Throttled scroll tracking using requestAnimationFrame
    let scrollRafId = null;
    const handleScroll = () => {
      if (!scrollRafId) {
        scrollRafId = requestAnimationFrame(() => {
          this.currentScrollY = window.pageYOffset;
          this.isScrolling = true;

          clearTimeout(this.scrollTimeout);
          this.scrollTimeout = setTimeout(() => {
            this.isScrolling = false;
          }, 150);
          
          scrollRafId = null;
        });
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Resize handler
    window.addEventListener('resize', this.throttle(() => {
      this.updateElementPositions();
    }, 300));

    // Intersection Observer for viewport detection
    this.setupIntersectionObserver();
  }

  setupIntersectionObserver() {
    const observerOptions = {
      root: null,
      rootMargin: '20% 0px',
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1]
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const element = entry.target;
        const data = this.elements.find(el => el.element === element);
        
        if (data) {
          data.isVisible = entry.isIntersecting;
          data.intersectionRatio = entry.intersectionRatio;

          // Trigger entrance animations
          if (entry.isIntersecting && !element.classList.contains('parallax-visible')) {
            element.classList.add('parallax-visible');
            this.triggerEntranceAnimation(data);
          }
        }
      });
    }, observerOptions);

    this.elements.forEach(data => {
      this.observer.observe(data.element);
    });
  }

  triggerEntranceAnimation(data) {
    const { element, options, index } = data;
    
    if (options.stagger) {
      const delay = index * (options.delay || 100);
      setTimeout(() => {
        element.classList.add('animate-in');
      }, delay);
    } else {
      element.classList.add('animate-in');
    }
  }

  startAnimation() {
    const animate = () => {
      // Smooth scroll interpolation
      this.smoothScrollY += (this.currentScrollY - this.smoothScrollY) * this.scrollSpeed;

      // Update all parallax elements
      this.updateElements();

      this.rafId = requestAnimationFrame(animate);
    };

    animate();
  }

  updateElements() {
    const scrollY = this.smoothScrollY;
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    this.elements.forEach(data => {
      if (!data.isVisible && !data.options.loop) return;

      const { element, options, originalY, originalX, height } = data;
      const elementTop = originalY - scrollY;
      const elementBottom = elementTop + height;
      const elementCenter = elementTop + height / 2;
      const screenCenter = windowHeight / 2;
      const progress = (screenCenter - elementCenter) / windowHeight;

      // Base parallax movement
      let yOffset = 0;
      let xOffset = 0;
      let scale = 1;
      let rotation = 0;
      let opacity = 1;

      if (options.speed !== 1) {
        yOffset = -(scrollY - originalY) * (1 - options.speed);
      }

      // Fade effects
      if (options.fadeOut) {
        opacity = Math.max(0, 1 - Math.abs(progress * 2));
      } else if (options.fadeIn) {
        opacity = Math.min(1, (windowHeight - elementTop) / windowHeight);
      }

      // Scale effects
      if (options.scale) {
        scale = 0.9 + (0.1 * opacity);
      }

      // Rotation effects
      if (options.rotate) {
        rotation = progress * 10;
      } else if (options.rotate3d) {
        const rotateX = progress * 5;
        const rotateY = Math.sin(scrollY * 0.001) * 5;
        element.style.transform = `
          perspective(${options.perspective || 1000}px)
          translate3d(${xOffset}px, ${yOffset}px, 0)
          rotateX(${rotateX}deg)
          rotateY(${rotateY}deg)
          scale(${scale})
        `;
        element.style.opacity = opacity;
        return;
      }

      // Tilt effect on scroll
      if (options.tilt && this.isScrolling) {
        const tiltAmount = Math.min(Math.max((this.currentScrollY - this.lastScrollY) * 0.5, -10), 10);
        rotation = tiltAmount;
      }

      // Apply transformations
      const transform = `translate3d(${xOffset}px, ${yOffset}px, 0) scale(${scale}) rotate(${rotation}deg)`;
      element.style.transform = transform;
      element.style.opacity = opacity;

      // Update text animations
      if (options.splitText) {
        const words = element.querySelectorAll('.parallax-word');
        words.forEach((word, i) => {
          const wordProgress = Math.max(0, Math.min(1, (progress + 0.5) * 2));
          word.style.transform = `translateY(${(1 - wordProgress) * 20}px)`;
          word.style.opacity = wordProgress;
        });
      }
    });

    this.lastScrollY = this.currentScrollY;
  }

  updateElementPositions() {
    this.elements.forEach(data => {
      const rect = data.element.getBoundingClientRect();
      data.originalY = rect.top + window.pageYOffset;
      data.originalX = rect.left + window.pageXOffset;
      data.height = rect.height;
      data.width = rect.width;
    });
  }

  throttle(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  destroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (!prefersReducedMotion) {
    window.figgyzParallax = new ParallaxEngine();
  } else {
    // Add simple fade-in animations only
    document.body.classList.add('reduced-motion');
  }

  // Add smooth scroll behavior
  document.documentElement.style.scrollBehavior = 'smooth';
});